/**
 * How a Resend delivery status reads to staff (#3787).
 *
 * Only `delivered` is a success. `sent` is the provider's acceptance, not
 * arrival; `delayed` is still in flight; `complained` means the recipient
 * marked it as spam and the address is suppressed from then on; `suppressed`
 * was never sent. A status this build does not know is unknown, never delivered.
 */
import type { OutboundEmail } from '@/hooks/useOutboundEmails';

export type EmailDeliveryTone = 'success' | 'info' | 'warning' | 'error' | 'unknown';

export interface EmailDeliveryPresentation {
  /** Key in the `common` namespace. */
  labelKey: string;
  hintKey: string;
  tone: EmailDeliveryTone;
}

const KNOWN: Record<string, { tone: EmailDeliveryTone }> = {
  sent: { tone: 'info' },
  delayed: { tone: 'warning' },
  delivered: { tone: 'success' },
  bounced: { tone: 'error' },
  complained: { tone: 'warning' },
  failed: { tone: 'error' },
  suppressed: { tone: 'error' },
};

export function emailDeliveryPresentation(status: string | null | undefined): EmailDeliveryPresentation {
  const known = status != null && Object.hasOwn(KNOWN, status) ? KNOWN[status] : undefined;
  if (!known || status == null) {
    return { labelKey: 'emailDelivery.status.unknown', hintKey: 'emailDelivery.hint.unknown', tone: 'unknown' };
  }
  return {
    labelKey: `emailDelivery.status.${status}`,
    hintKey: `emailDelivery.hint.${status}`,
    tone: known.tone,
  };
}

/** Delivery evidence is a row; the time it arrived counts only while the status still says so. */
export function deliveredAtToShow(email: OutboundEmail): string | null {
  return email.status === 'delivered' ? email.deliveredAt : null;
}
